import type {ReactElement} from 'react';
import {useCallback, useContext, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {formatToNearestThousand} from '@Utils/number.util';
import {isUserPremium} from '@Libraries/user.library';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useRemoveBackgroundCredits} from '@EmailMakerReact/lib/email-maker-background-removal-library';
import type {ModListItem as ModListItemData} from '../../mod-list-control.types';
import {MOD_LIST_ACTION_AI_IMAGES, MOD_LIST_ACTION_REMOVE_BACKGROUND} from '../../mod-list-control.types';
import {ModListSubOptions} from '../mod-list-sub-options/mod-list-sub-options';
import styles from './mod-list-item.module.scss';

interface ModListItemProps {
  item: ModListItemData;
  isSelected: boolean;
}

function renderCreditsTag(removeBackgroundCredits: number, userIsPremium: boolean): ReactElement | null {
  const showRemainingBgRemovals = removeBackgroundCredits > 0;

  if (userIsPremium) {
    if (!showRemainingBgRemovals) {
      return null;
    }

    return (
      <div
        className="bg-removals-tag js-bg-credits radius-round content-body-white _fit-width flex-center bg-secondary-150"
        data-is-modern-tooltip="1"
        rel="tooltip"
        title={window.i18next.t('pmwjs_credits_cost_and_remaining_info', {creditsRemaining: removeBackgroundCredits, creditsCost: 1})}
      >
        <Text val={formatToNearestThousand(removeBackgroundCredits)} size={TextSize.SM} bold />
      </div>
    );
  }

  return <Icon aria-hidden className="color-premium" icon="icon-crown" size={IconSize.SIZE_ICON_20} />;
}

function shouldShowTag(item: ModListItemData, userIsPremium: boolean): boolean {
  if (item.action === MOD_LIST_ACTION_REMOVE_BACKGROUND) {
    return true;
  }

  if (item.action === MOD_LIST_ACTION_AI_IMAGES && !userIsPremium) {
    return true;
  }

  return false;
}

export function ModListItem({item, isSelected}: ModListItemProps): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const [showSubOptions, setShowSubOptions] = useState(false);
  const hasSubOptions = Boolean(item.subOptions?.length);
  const userIsPremium = isUserPremium();
  const removeBackgroundCredits = useRemoveBackgroundCredits(editor);

  const handleClick = useCallback((): void => {
    if (!editor) {
      return;
    }

    const component = editor.getSelected();
    if (!component) {
      return;
    }

    if (hasSubOptions) {
      setShowSubOptions(true);
      return;
    }

    void item.callback(editor, component);
  }, [editor, item, hasSubOptions]);

  const handleCloseSubOptions = useCallback((): void => {
    setShowSubOptions(false);
  }, []);

  /** Premium/credits tag overlay — null unless this item's action warrants it. */
  function renderTag(): ReactElement | null {
    if (!shouldShowTag(item, userIsPremium)) {
      return null;
    }

    return <div className={`tag _position-absolute ${styles.tag}`}>{renderCreditsTag(removeBackgroundCredits, userIsPremium)}</div>;
  }

  if (showSubOptions && item.subOptions) {
    return (
      <li className={`mod-list-item ${styles.item} ${styles.itemFullWidth}`}>
        <ModListSubOptions item={item} onClose={handleCloseSubOptions} />
      </li>
    );
  }

  return (
    <li className={`mod-list-item _cursor-pointer hover-transition-all ${styles.item} ${isSelected ? '-selected' : ''}`}>
      <button aria-label={item.label} className={`mod-list-item-div _full-height ${styles.btn}`} onClick={handleClick} title={item.label} type="button">
        <Icon aria-hidden className="mod-list-item-icon" icon={item.iconClass} size={IconSize.SIZE_ICON_24} />
        <Text val={item.label} size={TextSize.SM} className={`mod-list-item-label ${item.label ? '' : '_hidden'}`} />
      </button>
      {renderTag()}
    </li>
  );
}
